// demo-source.js — chooses the state source for the bingocall pipeline.
// 1) Try the live DauberDemo engine (server-synced, lockstep with phones).
// 2) On enabled:false → dispatch a one-time off-state (unified with DEMO_ENABLED).
// 3) On script load failure / init error / no-frame timeout → local fallback loop.

import { mapDemoState, stateSignature } from './demo-state.js';
import * as fallback from './bingo-fallback.js';

var SCRIPT_URL = 'https://app.dauberhq.com/assets/js/demo-board.min.js';
var ENDPOINT = 'https://app.dauberhq.com/game/api/demo-state';
var LOAD_TIMEOUT = 4000;   // ms to first frame before we fall back
var SUBSCRIBER_SELECTOR = '[data-bingo-board], [data-bingo-ball], [data-bingo-history], [data-bingo-calltext]';

var lastSig = null;
var gotFrame = false;
var reduced = false;
var reducedRendered = false;
var watchdog = null;

function dispatch(detail) {
  document.dispatchEvent(new CustomEvent('bingocall', { detail: detail }));
}

function onFrame(demoState) {
  gotFrame = true;
  if (watchdog) { clearTimeout(watchdog); watchdog = null; }
  if (fallback.isRunning()) fallback.stop(); // engine took over
  if (document.hidden) return;               // resyncs automatically next frame
  // Reduced motion: render exactly one engine frame, then ignore the rest.
  // (The engine exposes no unsubscribe, so we gate here rather than detach.)
  if (reduced && reducedRendered) return;

  var mapped = mapDemoState(demoState);
  mapped.roundId = demoState ? demoState.roundId : undefined;
  var sig = stateSignature(mapped);
  if (sig === lastSig) return;
  lastSig = sig;
  dispatch(mapped);

  if (reduced) {
    reducedRendered = true;
    // Best-effort detach if the engine ever exposes one (harmless otherwise).
    try { if (window.DauberDemo && DauberDemo.unsubscribe) DauberDemo.unsubscribe(onFrame); } catch (_) {}
  }
}

function startEngine() {
  try {
    window.DauberDemo.init({ endpoint: ENDPOINT });
    window.DauberDemo.subscribe(onFrame);
    // If the first frame never arrives, fall back.
    watchdog = setTimeout(function () { if (!gotFrame) fallback.start(); }, LOAD_TIMEOUT);
  } catch (_) {
    fallback.start();
  }
}

function loadEngine() {
  var s = document.createElement('script');
  s.src = SCRIPT_URL;
  s.async = true;
  s.onload = function () {
    if (window.DauberDemo && typeof window.DauberDemo.init === 'function') startEngine();
    else fallback.start();
  };
  s.onerror = function () { fallback.start(); };
  document.head.appendChild(s);
  // Hard timeout in case onload/onerror never fire (e.g. blocked silently).
  setTimeout(function () { if (!gotFrame && !fallback.isRunning() && !window.DauberDemo) fallback.start(); }, LOAD_TIMEOUT + 1000);
}

function init() {
  if (!document.querySelector(SUBSCRIBER_SELECTOR)) return;
  reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  loadEngine();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
